import { useQuery } from "@apollo/client";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { FilePreview } from "components/Document/shared/FilePreview";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";

export interface DocumentPreviewPaneProps {
  document: { id: string; fileExtension?: string | null };
}

/**
 * Fetches a document's signed download URL and renders the shared preview
 * body: skeleton while loading, an error state with Retry when the link
 * can't be fetched, and FilePreview once the URL is ready. Fills its
 * parent's height.
 *
 * The download link is an S3 presigned URL that expires (~1h), so it must
 * not be served from the Apollo cache: a cached link handed to a later
 * open could already be dead, and the failure would surface inside the PDF
 * viewer (a broken render) rather than through this component's error/Retry
 * path. `network-only` fetches a fresh link on every mount, matching the
 * rail's prior behavior.
 */
export function DocumentPreviewPane({ document }: DocumentPreviewPaneProps) {
  const { data, loading, error, refetch } = useQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    variables: { id: document.id },
    fetchPolicy: "network-only",
  });
  const fileUrl = data?.getDocumentDownloadLink;

  if (loading) {
    return <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />;
  }

  if (error || !fileUrl) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%" }}>
        <Typography variant="body2" color="text.secondary">
          Couldn't load the document preview.
        </Typography>
        {/* Swallow the rejection: a still-failing refetch (Apollo's default
            errorPolicy rejects) would otherwise surface as an unhandled
            rejection. The query's own `error` already drives this state. */}
        <Button size="small" onClick={() => void refetch().catch(() => {})}>
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <FilePreview
      fileUrl={fileUrl}
      fileExtension={document.fileExtension ?? "pdf"}
      defaultScale="page-fit"
    />
  );
}
